import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Sparkles, Tag, Clock, Calendar, Hash, Percent, DollarSign, ShieldCheck } from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function SpecialsPage() {
  const [specials, setSpecials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [diningTypes, setDiningTypes] = useState([]);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return {
      name: "", display_name: "", receipt_text: "",
      discount_type: "amount", discount_amount: "", variable_discount: false,
      is_active: true, display_on_pos: true, auto_apply: false, pin_required: false, taxed: true,
      apply_to: "order", target_product_ids: [], target_category_ids: [],
      how_often: "once_per_order", stacked_type: "competitive",
      start_date: "", end_date: "", time_start: "", time_end: "",
      days_of_week: [],
      order_type_ids: [],
      discount_code: "", barcode: "", max_uses: "", current_uses: 0,
    };
  }

  const fetchData = useCallback(async () => {
    try {
      const [sRes, cRes, pRes, dtRes] = await Promise.all([
        api.get("/specials"), api.get("/categories"), api.get("/products?active_only=true"), api.get("/dining-types"),
      ]);
      setSpecials(sRes.data);
      setCategories(cRes.data);
      setProducts(pRes.data);
      setDiningTypes(dtRes.data.filter((d) => d.is_active));
    } catch { toast.error("Failed to load specials"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setEditing(null); setForm(defaultForm()); setShowDialog(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name, display_name: s.display_name || "", receipt_text: s.receipt_text || "",
      discount_type: s.discount_type || "amount", discount_amount: String(s.discount_amount || ""),
      variable_discount: s.variable_discount || false,
      is_active: s.is_active !== false, display_on_pos: s.display_on_pos !== false,
      auto_apply: s.auto_apply || false, pin_required: s.pin_required || false, taxed: s.taxed !== false,
      apply_to: s.apply_to || "order", target_product_ids: s.target_product_ids || [],
      target_category_ids: s.target_category_ids || [],
      how_often: s.how_often || "once_per_order", stacked_type: s.stacked_type || "competitive",
      start_date: s.start_date || "", end_date: s.end_date || "",
      time_start: s.time_start || "", time_end: s.time_end || "",
      days_of_week: s.days_of_week || [], order_type_ids: s.order_type_ids || [],
      discount_code: s.discount_code || "", barcode: s.barcode || "",
      max_uses: String(s.max_uses || ""), current_uses: s.current_uses || 0,
    });
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.discount_amount && !form.variable_discount) { toast.error("Discount amount required"); return; }
    try {
      const payload = {
        ...form,
        discount_amount: parseFloat(form.discount_amount) || 0,
        max_uses: parseInt(form.max_uses) || 0,
      };
      if (editing) { await api.put(`/specials/${editing.special_id}`, payload); toast.success("Updated"); }
      else { await api.post("/specials", payload); toast.success("Created"); }
      setShowDialog(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this special?")) return;
    try { await api.delete(`/specials/${id}`); toast.success("Deleted"); fetchData(); }
    catch { toast.error("Failed"); }
  };

  const toggleDay = (day) => {
    setForm((prev) => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day) ? prev.days_of_week.filter((d) => d !== day) : [...prev.days_of_week, day],
    }));
  };

  const toggleOrderType = (dtId) => {
    setForm((prev) => ({
      ...prev,
      order_type_ids: prev.order_type_ids.includes(dtId) ? prev.order_type_ids.filter((id) => id !== dtId) : [...prev.order_type_ids, dtId],
    }));
  };

  const toggleTargetProduct = (pid) => {
    setForm((prev) => ({
      ...prev,
      target_product_ids: prev.target_product_ids.includes(pid) ? prev.target_product_ids.filter((id) => id !== pid) : [...prev.target_product_ids, pid],
    }));
  };

  const toggleTargetCategory = (cid) => {
    setForm((prev) => ({
      ...prev,
      target_category_ids: prev.target_category_ids.includes(cid) ? prev.target_category_ids.filter((id) => id !== cid) : [...prev.target_category_ids, cid],
    }));
  };

  if (loading) return <div className="p-8 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>;

  return (
    <div className="p-6" data-testid="specials-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight" data-testid="specials-title">Specials</h1>
          <p className="text-sm text-slate-500 mt-1">{specials.length} specials / discounts</p>
        </div>
        <Button onClick={openAdd} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="add-special-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Special
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="specials-list">
        <div className="grid grid-cols-[minmax(0,1fr)_100px_100px_120px_80px_80px_80px] gap-2 px-4 py-2 bg-slate-50 text-xs font-semibold text-slate-400 uppercase tracking-wider border-b">
          <div>Special</div><div className="text-center">Type</div><div className="text-right">Value</div><div className="text-center">Schedule</div><div className="text-center">Code</div><div className="text-center">Active</div><div className="text-right">Actions</div>
        </div>
        {specials.map((s) => (
          <div key={s.special_id} className="grid grid-cols-[minmax(0,1fr)_100px_100px_120px_80px_80px_80px] gap-2 items-center px-4 py-3 border-b border-slate-50 hover:bg-slate-50 group" data-testid={`special-row-${s.special_id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Sparkles className={`h-4 w-4 flex-shrink-0 ${s.is_active ? "text-amber-500" : "text-slate-300"}`} />
                <span className="text-sm font-medium text-slate-800 truncate">{s.name}</span>
                {s.auto_apply && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 py-0 px-1">AUTO</Badge>}
                {s.pin_required && <Badge className="text-[9px] bg-red-100 text-red-700 border-red-200 py-0 px-1">PIN</Badge>}
              </div>
              {s.apply_to === "product" && (s.target_product_ids?.length > 0 || s.target_category_ids?.length > 0) && (
                <p className="text-[10px] text-slate-400 ml-6">
                  {s.target_category_ids?.length > 0 && `${s.target_category_ids.length} categories`}
                  {s.target_category_ids?.length > 0 && s.target_product_ids?.length > 0 && " + "}
                  {s.target_product_ids?.length > 0 && `${s.target_product_ids.length} products`}
                </p>
              )}
            </div>
            <div className="text-center">
              <Badge variant="outline" className={`text-xs ${s.discount_type === "percentage" ? "text-purple-600 border-purple-200" : "text-blue-600 border-blue-200"}`}>
                {s.discount_type === "percentage" ? <Percent className="h-3 w-3 mr-0.5" /> : <DollarSign className="h-3 w-3 mr-0.5" />}
                {s.discount_type === "percentage" ? "%" : "$"}
              </Badge>
            </div>
            <div className="text-right text-sm font-mono font-semibold text-slate-700">
              {s.variable_discount ? "Variable" : s.discount_type === "percentage" ? `${s.discount_amount}%` : `$${(s.discount_amount || 0).toFixed(2)}`}
            </div>
            <div className="text-center">
              {s.days_of_week?.length > 0 && (
                <div className="flex gap-0.5 justify-center flex-wrap">
                  {s.days_of_week.map((d) => <span key={d} className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">{DAYS[d]}</span>)}
                </div>
              )}
              {s.time_start && s.time_end && <p className="text-[10px] text-slate-400">{s.time_start}-{s.time_end}</p>}
              {!s.days_of_week?.length && !s.time_start && <span className="text-xs text-slate-300">Always</span>}
            </div>
            <div className="text-center">
              {s.discount_code ? (
                <Badge variant="outline" className="text-[10px] font-mono">{s.discount_code}</Badge>
              ) : <span className="text-xs text-slate-300">—</span>}
              {s.max_uses > 0 && <p className="text-[9px] text-slate-400">{s.current_uses}/{s.max_uses}</p>}
            </div>
            <div className="text-center">
              <Badge variant={s.is_active ? "default" : "secondary"} className="text-xs">{s.is_active ? "Yes" : "No"}</Badge>
            </div>
            <div className="text-right flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => openEdit(s)} className="p-1 rounded hover:bg-blue-100" data-testid={`edit-special-${s.special_id}`}><Pencil className="h-3.5 w-3.5 text-[var(--brand-primary)]" /></button>
              <button onClick={() => remove(s.special_id)} className="p-1 rounded hover:bg-red-100" data-testid={`del-special-${s.special_id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></button>
            </div>
          </div>
        ))}
        {specials.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No specials yet. Create your first special or discount to get started.</div>}
      </div>

      {/* Special Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col" data-testid="special-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> {editing ? "Edit Special" : "New Special"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Configure discount details, scheduling, and targeting rules</DialogDescription>
          </DialogHeader>
          <ScrollArea className="flex-1 -mx-6 px-6">
            <div className="space-y-5 py-2">
              {/* Basic Info */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Tag className="h-3 w-3" /> Basic Info</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Name *</Label>
                    <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Lunch Slice Deal" data-testid="special-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Display Name (POS)</Label>
                    <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="Shown on POS button" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Receipt Text</Label>
                  <Input value={form.receipt_text} onChange={(e) => setForm({ ...form, receipt_text: e.target.value })} placeholder="Text printed on receipt" />
                </div>
              </div>

              {/* Discount Value */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><DollarSign className="h-3 w-3" /> Discount Value</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Type</Label>
                    <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                      <SelectTrigger data-testid="special-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amount">$ Amount</SelectItem>
                        <SelectItem value="percentage">% Percentage</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Amount {form.discount_type === "percentage" ? "(%)" : "($)"}</Label>
                    <Input type="number" step="0.01" value={form.discount_amount} onChange={(e) => setForm({ ...form, discount_amount: e.target.value })} placeholder={form.discount_type === "percentage" ? "10" : "2.00"} data-testid="special-amount" disabled={form.variable_discount} />
                  </div>
                  <div className="flex items-end pb-2">
                    <div className="flex items-center gap-2">
                      <Switch checked={form.variable_discount} onCheckedChange={(v) => setForm({ ...form, variable_discount: v })} data-testid="special-variable" />
                      <Label className="text-xs text-slate-600">Variable (cashier enters)</Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Toggles */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Settings</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  {[
                    { key: "is_active", label: "Active" },
                    { key: "display_on_pos", label: "Display on POS" },
                    { key: "auto_apply", label: "Auto Apply" },
                    { key: "pin_required", label: "PIN Required" },
                    { key: "taxed", label: "Apply tax to discounted amount" },
                  ].map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-2">
                      <Switch checked={form[key]} onCheckedChange={(v) => setForm({ ...form, [key]: v })} data-testid={`special-${key}`} />
                      <Label className="text-sm text-slate-700">{label}</Label>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">How Often to Apply</Label>
                    <Select value={form.how_often} onValueChange={(v) => setForm({ ...form, how_often: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once_per_order">Once per order</SelectItem>
                        <SelectItem value="all_applicable">To all applicable items</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Stacked Discount Type</Label>
                    <Select value={form.stacked_type} onValueChange={(v) => setForm({ ...form, stacked_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="competitive">Competitive (best discount wins)</SelectItem>
                        <SelectItem value="cumulative">Cumulative (stack together)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Targeting */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Tag className="h-3 w-3" /> Targeting</p>
                <div>
                  <Label className="text-xs text-slate-500">Apply To</Label>
                  <Select value={form.apply_to} onValueChange={(v) => setForm({ ...form, apply_to: v })}>
                    <SelectTrigger data-testid="special-apply-to"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="order">Entire Order</SelectItem>
                      <SelectItem value="product">Specific Products / Categories</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {form.apply_to === "product" && (
                  <div className="space-y-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Target Categories</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {categories.map((c) => (
                          <label key={c.category_id} className={`flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer text-xs transition-colors ${form.target_category_ids.includes(c.category_id) ? "bg-blue-100 text-blue-700 border border-blue-200" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                            <Checkbox checked={form.target_category_ids.includes(c.category_id)} onCheckedChange={() => toggleTargetCategory(c.category_id)} className="h-3 w-3" />
                            {c.name}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Target Products</Label>
                      <div className="max-h-[120px] overflow-y-auto space-y-0.5 border border-slate-100 rounded-lg p-2">
                        {products.map((p) => (
                          <label key={p.product_id} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-xs ${form.target_product_ids.includes(p.product_id) ? "bg-blue-50 text-blue-700" : "hover:bg-slate-50 text-slate-600"}`}>
                            <Checkbox checked={form.target_product_ids.includes(p.product_id)} onCheckedChange={() => toggleTargetProduct(p.product_id)} className="h-3 w-3" />
                            <span className="truncate">{p.name}</span>
                            <span className="text-slate-400 ml-auto">${(p.price || 0).toFixed(2)}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {diningTypes.length > 0 && (
                  <div>
                    <Label className="text-xs text-slate-500 mb-1 block">Order Types (empty = all)</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {diningTypes.map((dt) => (
                        <label key={dt.dining_type_id} className={`flex items-center gap-1.5 px-2 py-1 rounded-md cursor-pointer text-xs transition-colors ${form.order_type_ids.includes(dt.dining_type_id) ? "bg-blue-100 text-blue-700 border border-blue-200" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                          <Checkbox checked={form.order_type_ids.includes(dt.dining_type_id)} onCheckedChange={() => toggleOrderType(dt.dining_type_id)} className="h-3 w-3" />
                          {dt.code} - {dt.name}
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Schedule */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Calendar className="h-3 w-3" /> Schedule</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Start Date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="special-start-date" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">End Date</Label>
                    <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="special-end-date" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Time Start</Label>
                    <Input type="time" value={form.time_start} onChange={(e) => setForm({ ...form, time_start: e.target.value })} data-testid="special-time-start" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Time End</Label>
                    <Input type="time" value={form.time_end} onChange={(e) => setForm({ ...form, time_end: e.target.value })} data-testid="special-time-end" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Days of Week (empty = every day)</Label>
                  <div className="flex gap-1.5">
                    {DAYS.map((label, idx) => (
                      <button key={idx} onClick={() => toggleDay(idx)} className={`w-10 h-8 rounded-md text-xs font-medium transition-colors ${form.days_of_week.includes(idx) ? "bg-[var(--brand-primary)] text-white shadow-sm" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`} data-testid={`special-day-${idx}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Discount Code */}
              <div className="space-y-3">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Hash className="h-3 w-3" /> Discount Code</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Code</Label>
                    <Input value={form.discount_code} onChange={(e) => setForm({ ...form, discount_code: e.target.value.toUpperCase() })} placeholder="e.g., LUNCH20" className="font-mono uppercase" data-testid="special-code" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Barcode</Label>
                    <Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} placeholder="Scannable barcode" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Max Uses (0 = unlimited)</Label>
                    <Input type="number" min="0" value={form.max_uses} onChange={(e) => setForm({ ...form, max_uses: e.target.value })} placeholder="0" data-testid="special-max-uses" />
                  </div>
                </div>
                {editing && form.max_uses > 0 && (
                  <p className="text-xs text-slate-400">Current uses: <strong>{form.current_uses}</strong> / {form.max_uses}</p>
                )}
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="pt-3 border-t border-slate-100">
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="special-save-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
